import { useCallback, useRef } from 'react';
import { useLang } from 'hooks/lang';

import { actions, isOwner, FullTask, getTaskStatusConfig, taskStatusesConfig } from 'entities';

import { Row, Tooltip, Input, Loader } from 'components/Blocks';

import { PencilIcon } from 'components/Icons';

import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';
import { useToggle, useUser, useWatch } from 'hooks';
import { useTaskModal } from '../../context.taskModal';

import { cn } from 'styles';
import { cascadeEval } from 'utils/conditions';

interface SubTaskNameProps {
  children?: never;
  subTask: FullTask['subTasks'][number];
}

export function SubTaskName({ subTask }: SubTaskNameProps) {
  const { t } = useLang();
  const loggedUser = useUser();

  const { task } = useTaskModal();

  const ref = useRef<HTMLInputElement>(null);

  const { isOn, turnOff, turnOn } = useToggle();

  const { project, updateProject } = useProjectProfile();

  const openInput = useCallback(() => {
    turnOn();

    setTimeout(() => ref.current?.focus(), 50);
  }, [turnOn]);

  const closeInput = useCallback(() => {
    turnOff();

    setTimeout(() => ref.current?.blur(), 50);
  }, [turnOff]);

  const changeName = actions.projects.changeSubTaskName.use({
    onSuccess: (_, { name }) => {
      updateProject((old) => ({
        ...old,
        tasks: old.tasks.map((projTask) =>
          projTask.id !== task.id
            ? projTask
            : {
                ...projTask,
                subTasks: projTask.subTasks.map((sub) =>
                  sub.id !== subTask.id ? sub : { ...sub, name },
                ),
              },
        ),
      }));
    },
  });

  const handleSave = useCallback(async () => {
    if (!ref.current) return closeInput();

    const name = ref.current?.value.trim();

    if (!name || subTask.name === name) return closeInput();

    const success = await changeName.execute({
      project: project.id,
      task: task.id,
      subTask: subTask.id,
      name,
    });

    if (success) closeInput();
  }, [closeInput, project.id, task, subTask, changeName]);

  // realtime changes
  useWatch(subTask.name, (name) => {
    if (ref.current && ref.current.value !== name) {
      ref.current.value = name;
    }
  });

  const statusConfig = getTaskStatusConfig(task.status);

  const canEdit =
    !statusConfig.blocks.fullBlock &&
    subTask.status !== taskStatusesConfig.done.code &&
    isOwner(subTask, loggedUser);

  return (
    <Row gap="2">
      {cascadeEval([
        {
          is: isOn && !changeName.loading,
          then: () => (
            <Input
              ref={ref}
              disabled={!canEdit}
              minLength={180}
              onBlur={handleSave}
              onKeyDown={({ key }) => key === 'Enter' && handleSave()}
              defaultValue={subTask.name}
              className="h-auto w-full py-1"
            />
          ),
        },
        {
          is: changeName.loading,
          then: () => <Loader type="dualRing" />,
        },
        {
          is: true,
          then: () => (
            <Tooltip
              content={
                <Row gap="2">
                  <PencilIcon size={16} />
                  <span className="font-medium">{t('edit')}</span>
                </Row>
              }
            >
              <span
                onClick={openInput}
                className={cn('transition-opacity', canEdit && 'cursor-pointer hover:opacity-80')}
              >
                {subTask.name}
              </span>
            </Tooltip>
          ),
        },
      ])}
    </Row>
  );
}
